import { Observable } from 'rxjs';
import { HttpEvent } from '@angular/common/http';
import {
  KybContactInfo,
  KybCompanyInfo,
  KybCompanyTypeOption,
  KybSatInfo,
  KybLegalRepresentative,
  KybBeneficialOwner,
  KybBusinessContact,
  KybSpouseDependent,
  KybResourceProvidersToggle,
  KybResourceProvidersBulkRequest,
  KybResourceProvidersBulkResponse,
  KybBeneficiariesBulkRequest,
  KybPaymentBeneficiariesBulkRequest,
  KybOperationalInfo,
  KybPosMerchantInfo,
  KybSolicitantForm,
  KybShareholderFormData,
  KybOrgChart,
  KybClientsProvidersInfo,
  KybDocumentUrlRequest,
  KybShareholder,
  KybShareholderBulkRequest,
  KybApiResponse,
  KybProcessState,
} from '@app/onboarding/kyb/interfaces/kyb';

/**
 * Puerto del dominio para la API de Onboarding KYB (PFAE y PM+SUITE).
 *
 * Los componentes inyectan esta abstracción en lugar del HttpClient o del
 * `KybApiService` concreto. La implementación se intercambia vía DI:
 * - `KybApiService` (default, providedIn: 'root') — bridge actual con mocks
 *   embebidos. Se mantiene mientras se completa la migración a las
 *   data-sources separadas.
 * - `KybRemoteDataSource` (futuro) — HTTP puro a backend.
 * - `KybMockDataSource` (futuro) — fixtures de revisión sin red.
 *
 * Convenciones:
 * - El método `getId()` expone el id de proceso almacenado; se mantiene aquí
 *   para no romper consumidores que lo usan como guard antes de invocar
 *   endpoints scoped al proceso (ej. en `hydrateFromBackend`).
 * - Los wrappers tipan `Observable<any>` donde el contrato backend devuelve
 *   `{ data, success, code, message }` envuelto distinto por endpoint. Se irá
 *   tipando endpoint a endpoint en migraciones futuras.
 */
export abstract class KybRepository {
  /** Paso 0 — Crear proceso. */
  abstract startOnboarding(personType: string, product: string): Observable<any>;

  /** Pantalla 1 — Información de contacto. */
  abstract saveContactInfo(data: KybContactInfo): Observable<any>;

  /** Pantalla 2 (PM) — Datos de la empresa. */
  abstract saveCompanyInfo(data: KybCompanyInfo): Observable<any>;

  /** Catálogo `companyType` aceptado por backend. */
  abstract getCompanyTypes(): Observable<KybApiResponse<KybCompanyTypeOption[]>>;

  /** Pantalla 3 — Vinculación SAT. */
  abstract saveSatInfo(data: KybSatInfo): Observable<any>;

  /** Pantalla 4.A — Validar CURP (opcional). */
  abstract validateCurp(curp: string): Observable<any>;

  /** Pantalla 4.B — Guardar representante legal. */
  abstract saveLegalRepresentative(data: KybLegalRepresentative): Observable<any>;

  /** Pantalla 5 (PFAE) — Guardar beneficiario real. */
  abstract saveBeneficialOwner(data: KybBeneficialOwner): Observable<any>;

  /** Contacto del negocio (SUITE — 5.2 PFAE / 7 PM). */
  abstract saveBusinessContact(data: KybBusinessContact): Observable<any>;

  /** Cónyuge / dependientes económicos (PFAE+SUITE — 5.3). */
  abstract saveSpouseDependent(data: KybSpouseDependent): Observable<any>;

  /** Toggle de proveedores externos (SUITE PM / CARDS). */
  abstract toggleResourceProviders(data: KybResourceProvidersToggle): Observable<any>;

  /** Bulk de proveedores de recursos (SUITE). */
  abstract bulkResourceProviders(
    data: KybResourceProvidersBulkRequest,
  ): Observable<KybApiResponse<KybResourceProvidersBulkResponse>>;

  /** Bulk de beneficiarios (SUITE — 5.6 PFAE / 7 PM). */
  abstract bulkBeneficiaries(data: KybBeneficiariesBulkRequest): Observable<any>;

  /** Bulk de beneficiarios de PAGO (CARDS). */
  abstract bulkPaymentBeneficiaries(data: KybPaymentBeneficiariesBulkRequest): Observable<any>;

  /** Información operativa (SUITE). */
  abstract saveOperationalInfo(data: KybOperationalInfo): Observable<any>;

  /** Información operativa POS. */
  abstract savePosMerchantInfo(data: KybPosMerchantInfo): Observable<any>;

  /** Formulario PLD/KYC del solicitante (FORM_INFO). */
  abstract saveSolicitantForm(data: KybSolicitantForm): Observable<any>;

  /** Datos del formulario PLD/KYC por accionista (PM+SUITE — FORM_INFO 8.2). */
  abstract saveShareholderFormData(data: KybShareholderFormData): Observable<any>;

  /** Organigrama empresarial (PM+SUITE — FORM_INFO 8.3). */
  abstract saveOrgChart(data: KybOrgChart): Observable<any>;

  /** Información clientes-proveedores (PM+SUITE — FORM_INFO 8.4). */
  abstract saveClientsProvidersInfo(data: KybClientsProvidersInfo): Observable<any>;

  /** Documentos — Pedir URL prefirmada (`documents/presigned-url`). */
  abstract getDocumentUploadUrl(data: KybDocumentUrlRequest): Observable<any>;

  /** Documentos — PUT directo a S3. */
  abstract uploadToS3(s3Url: string, file: File, contentType: string): Observable<HttpEvent<unknown>>;

  /** URL firmada (TTL corto) para visualizar un documento ya subido. */
  abstract getDocumentDownloadUrl(documentId: number): Observable<any>;

  /** Documentos — Confirmar documento (FKs y ownerScope opcionales). */
  abstract confirmDocumentUpload(
    documentType: string,
    s3Key: string,
    fks?: {
      shareholderId?: number;
      resourceProviderId?: number;
      beneficiaryId?: number;
      paymentBeneficiaryId?: number;
      ownerScope?: string;
    },
  ): Observable<any>;

  /** Bulk de accionistas con sus docs (PM+SUITE). */
  abstract bulkCreateShareholders(data: KybShareholderBulkRequest): Observable<any>;

  /** Listar accionistas del proceso. */
  abstract listShareholders(): Observable<any>;

  /** Agregar un accionista individual (fallback al bulk). */
  abstract createShareholder(data: KybShareholder): Observable<any>;

  /** Eliminar un accionista por id. */
  abstract deleteShareholder(shareholderId: number): Observable<any>;

  /** Verificación de teléfono — enviar código SMS. */
  abstract sendPhoneCode(phone: string): Observable<any>;

  /** Verificación de teléfono — verificar código. */
  abstract verifyPhoneCode(phone: string, code: string): Observable<any>;

  /** Avanzar paso. */
  abstract nextStep(): Observable<any>;

  /** Enviar solicitud. */
  abstract submit(): Observable<any>;

  /** Utilitarios. */
  abstract getSteps(): Observable<any>;
  abstract getCurrentStep(): Observable<any>;

  /** Rehidrata el estado completo del proceso (load inicial o F5). */
  abstract getState(): Observable<KybApiResponse<KybProcessState>>;

  /** Id del proceso almacenado (guard para endpoints scoped). */
  abstract getId(): string;
}
